// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import examples from 'libphonenumber-js/examples.mobile.json'; // examples file is just 4KB
import type { ItemPropTypes } from '@george-labs.com/design-system/types/components/Dropdown';

import { getEnv } from '@luna/env';
import { t } from '@luna/i18n';

import type {
    CountryCode,
    GetExampleNumber,
    IsSupportedCountry,
    ParsePhoneNumberFromString,
} from './LiphoneApiTypes';

export interface PhoneInputNumber {
    countryCode: CountryCode;
    nationalNumber: string;
}

function mapCountriesToSelectItems(countries: any[] = []): ItemPropTypes[] {
    return countries.map((country) => ({
        value: country.countryIso,
        label: country.name,
    }));
}

export function getDefaultCountry(): CountryCode {
    return getEnv().country as CountryCode;
}

export function getPhoneSupportedCountryItems(
    isSupportedCountry: IsSupportedCountry,
    countries: any[] = [],
): ItemPropTypes[] {
    return mapCountriesToSelectItems(
        countries.filter((country: any) => isSupportedCountry(country.countryIso)),
    );
}

export function getFormattedPhoneNumber(
    parsePhoneNumberFromString: ParsePhoneNumberFromString,
    { nationalNumber, countryCode }: PhoneInputNumber,
): PhoneInputNumber {
    if (!nationalNumber) {
        return {
            countryCode,
            nationalNumber: '',
        };
    }

    const phoneNumber = parsePhoneNumberFromString(nationalNumber, countryCode);
    if (!phoneNumber) {
        return {
            countryCode,
            nationalNumber,
        };
    }

    return {
        countryCode: phoneNumber.country || getDefaultCountry(),
        nationalNumber: phoneNumber.formatNational(),
    };
}

export function getInitialFormattedPhoneNumber(
    parsePhoneNumberFromString: ParsePhoneNumberFromString,
    number: string | null | undefined,
): PhoneInputNumber {
    const phoneNumber = parsePhoneNumberFromString(number ?? '');

    if (!phoneNumber || !phoneNumber.isValid()) {
        return {
            countryCode: phoneNumber?.country ?? getDefaultCountry(),
            nationalNumber: phoneNumber?.nationalNumber ?? '',
        };
    }

    return {
        countryCode: phoneNumber.country!,
        nationalNumber: phoneNumber.formatNational(),
    };
}

export function getPhoneNumber(
    parsePhoneNumberFromString: ParsePhoneNumberFromString,
    number: string,
    countryCode: CountryCode,
): PhoneInputNumber {
    const phoneNumber = parsePhoneNumberFromString(number, countryCode);

    if (phoneNumber?.country) {
        return {
            countryCode: phoneNumber.country,
            nationalNumber: number,
        };
    }

    return { countryCode, nationalNumber: number };
}

const NOT_PARSABLE_PHONE_NUMBER = 'NOT_PARSABLE_PHONE_NUMBER';

export function getInternationalPhoneNumber(
    parsePhoneNumberFromString: ParsePhoneNumberFromString,
    { nationalNumber, countryCode }: PhoneInputNumber,
): string {
    const phoneNumber = parsePhoneNumberFromString(nationalNumber, countryCode);

    if (!phoneNumber) {
        return nationalNumber ? NOT_PARSABLE_PHONE_NUMBER : '';
    }

    return phoneNumber.number as string;
}

export function getCorrectNumberInfoAlert(
    getExampleNumber: GetExampleNumber,
    isPhoneInputTouched: boolean,
    countryCode: CountryCode,
    error: string | undefined,
): string | undefined {
    if (
        isPhoneInputTouched &&
        error === t('contactDataManagement.phoneContactForm.numberIncorrect')
    ) {
        return t('contactDataManagement.phoneContactForm.correctNumberExample', {
            number: getExampleNumber(countryCode, examples)?.formatNational(),
        });
    }

    return undefined;
}
